import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import NotificationDropdown from './NotificationDropdown';
import { Menu, X, ChevronDown, ChevronRight, LayoutDashboard, ShoppingBag, Star, Users, FileText, CreditCard, Settings, Package, MessageCircle, Moon, Sun, Boxes, Globe } from 'lucide-react';
import { db } from '../firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';

export default function AdminLayout() {
  const { currentUser, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [showWebsiteMenu, setShowWebsiteMenu] = useState(false);
  const [showProductsMenu, setShowProductsMenu] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Mobile sub-menus state
  const [mobileWebsiteMenuOpen, setMobileWebsiteMenuOpen] = useState(false);
  const [mobileProductsMenuOpen, setMobileProductsMenuOpen] = useState(false);

  // Order counts
  const [activeOrdersCount, setActiveOrdersCount] = useState(0);
  const [activeCustomOrdersCount, setActiveCustomOrdersCount] = useState(0);

  React.useEffect(() => {
    // Subscribe to active orders count
    const ordersQuery = query(
      collection(db, 'orders'),
      where('status', 'not-in', ['DISPATCHED', 'DELIVERED', 'CANCELLED'])
    );

    const unsubscribeOrders = onSnapshot(ordersQuery, (snapshot) => {
      // Exclude soft-deleted orders
      const activeCount = snapshot.docs.filter(doc => !doc.data().deleted).length;
      setActiveOrdersCount(activeCount);
    });

    // Subscribe to active custom orders count
    const customOrdersQuery = query(
      collection(db, 'customOrders'),
      where('status', 'not-in', ['DISPATCHED', 'DELIVERED', 'CANCELLED'])
    );

    const unsubscribeCustomOrders = onSnapshot(customOrdersQuery, (snapshot) => {
      const activeCustomCount = snapshot.docs.filter(doc => !doc.data().deleted).length;
      setActiveCustomOrdersCount(activeCustomCount);
    });

    return () => {
      unsubscribeOrders();
      unsubscribeCustomOrders();
    };
  }, []);

  async function handleSignOut() {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  }

  const NavItem = ({ to, children, end = false, icon: Icon, className = '', badge }) => (
    <NavLink
      to={to}
      end={end}
      onClick={() => setIsMobileMenuOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors relative ${isActive
          ? 'bg-gray-800 text-white'
          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
        } ${className}`
      }
    >
      {Icon && <Icon className="w-4 h-4" />}
      {children}
      {badge > 0 && (
        <span className="absolute top-1 right-1 flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-red-600 rounded-full">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col transition-colors duration-200">
      {/* Top Navigation Bar */}
      <nav className="bg-gray-900 text-white shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">

            {/* Logo & Mobile Menu Button */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="lg:hidden p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-800 focus:outline-none"
              >
                <Menu className="h-6 w-6" />
              </button>
              <h1 className="text-xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                Merch Masters
              </h1>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-2">
              <NavItem to="/admin" end icon={LayoutDashboard}>Dashboard</NavItem>
              <NavItem to="/admin/orders" icon={ShoppingBag} badge={activeOrdersCount}>Orders</NavItem>
              <NavItem to="/admin/custom-orders" icon={Star} className="text-purple-400 hover:text-purple-300" badge={activeCustomOrdersCount}>Custom</NavItem>
              <NavItem to="/admin/resellers" icon={Users}>Resellers</NavItem>

              <NavItem to="/admin/blog" icon={FileText}>Blog</NavItem>
              <NavItem to="/admin/financials" icon={CreditCard} className="text-green-400 hover:text-green-300">Financials</NavItem>
              <NavItem to="/admin/products" icon={Package}>Products</NavItem>
              <NavItem to="/admin/stock" icon={Boxes} className="text-orange-400 hover:text-orange-300">Stock</NavItem>
              <NavItem to="/admin/google-merchant" icon={Globe} className="text-blue-400 hover:text-blue-300">Google Merchant</NavItem>

              <div className="relative">
                <button
                  onClick={() => setShowWebsiteMenu(!showWebsiteMenu)}
                  onBlur={() => setTimeout(() => setShowWebsiteMenu(false), 200)}
                  className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-gray-300 hover:bg-gray-800 hover:text-white"
                >
                  <Settings className="w-4 h-4" />
                  Settings
                  <ChevronDown className="w-4 h-4" />
                </button>
                {showWebsiteMenu && (
                  <div className="absolute top-full right-0 mt-1 w-48 bg-white text-gray-900 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden z-50">
                    <NavLink to="/admin/website-info" className="block px-4 py-2 text-sm hover:bg-gray-100">Website Info</NavLink>

                    <NavLink to="/admin/holidays" className="block px-4 py-2 text-sm hover:bg-gray-100">Manage Holidays</NavLink>
                    <NavLink to="/admin/koombiyo-orders" className="block px-4 py-2 text-sm hover:bg-gray-100 border-b">Koombiyo Orders</NavLink>
                    <NavLink to="/admin/sms-settings" className="block px-4 py-2 text-sm hover:bg-gray-100 border-b">SMS Gateway</NavLink>
                    <NavLink to="/admin/whatsapp-inbox" className="block px-4 py-2 text-sm hover:bg-gray-100 bg-green-50 text-green-700 font-medium">💬 WhatsApp Inbox</NavLink>
                    <NavLink to="/admin/whatsapp-settings" className="block px-4 py-2 text-sm hover:bg-gray-100 border-b">Whatsapp Gateway</NavLink>
                    <NavLink to="/admin/whatsapp-ai-settings" className="block px-4 py-2 text-sm hover:bg-gray-100 bg-purple-50 text-purple-700 font-medium border-b">🤖 AI Settings</NavLink>
                    <NavLink to="/admin/custom-sms-broadcast" className="block px-4 py-2 text-sm hover:bg-gray-100 bg-blue-50 text-blue-700 font-medium">📢 SMS Broadcast</NavLink>
                    <NavLink to="/admin/users" className="block px-4 py-2 text-sm hover:bg-gray-100">Admin Users</NavLink>
                  </div>
                )}
              </div>
            </div>

            {/* User Profile & Actions */}
            <div className="flex items-center gap-4">
              {/* <div className="hidden md:block text-sm text-gray-300">
                {currentUser?.displayName || currentUser?.email}
              </div> */}
              <button
                onClick={toggleTheme}
                className="p-2 text-gray-400 hover:text-white rounded-full hover:bg-gray-800 transition-colors"
                title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <NotificationDropdown currentUser={currentUser} />
              <button
                onClick={handleSignOut}
                className="hidden md:block px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-md text-sm font-medium transition-colors"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation Drawer */}
      <div className={`fixed inset-0 z-50 lg:hidden transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />

        {/* Drawer */}
        <div className={`absolute top-0 left-0 bottom-0 w-80 bg-gray-900 text-white shadow-xl transform transition-transform duration-300 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="p-4 flex justify-between items-center border-b border-gray-800">
            <h2 className="text-xl font-bold">Menu</h2>
            <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-gray-400 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="p-4 space-y-2 overflow-y-auto max-h-[calc(100vh-80px)]">
            <NavItem to="/admin" end icon={LayoutDashboard}>Dashboard</NavItem>
            <NavItem to="/admin/orders" icon={ShoppingBag} badge={activeOrdersCount}>Orders</NavItem>
            <NavItem to="/admin/custom-orders" icon={Star} className="text-purple-400" badge={activeCustomOrdersCount}>Custom Orders</NavItem>
            <NavItem to="/admin/resellers" icon={Users}>Resellers</NavItem>

            <NavItem to="/admin/blog" icon={FileText}>Blog</NavItem>
            <NavItem to="/admin/financials" icon={CreditCard} className="text-green-400">Financials</NavItem>
            <NavItem to="/admin/products" icon={Package}>Products</NavItem>
            <NavItem to="/admin/stock" icon={Boxes} className="text-orange-400">Stock</NavItem>
            <NavItem to="/admin/google-merchant" icon={Globe} className="text-blue-400">Google Merchant</NavItem>



            {/* Mobile Settings Submenu */}
            <div className="pt-2">
              <button
                onClick={() => setMobileWebsiteMenuOpen(!mobileWebsiteMenuOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-300 hover:bg-gray-800 rounded-md"
              >
                <div className="flex items-center gap-3">
                  <Settings className="w-4 h-4" />
                  Settings
                </div>
                {mobileWebsiteMenuOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>

              {mobileWebsiteMenuOpen && (
                <div className="pl-10 space-y-1 mt-1">
                  <NavItem to="/admin/website-info" className="text-sm">Website Info</NavItem>
                  <NavItem to="/admin/products" className="text-sm">Products</NavItem>

                  <NavItem to="/admin/holidays" className="text-sm">Holidays</NavItem>
                  <NavItem to="/admin/sms-settings" className="text-sm">SMS Gateway</NavItem>
                  <NavItem to="/admin/whatsapp-inbox" className="text-sm text-green-400">💬 WhatsApp Inbox</NavItem>
                  <NavItem to="/admin/whatsapp-settings" className="text-sm">Whatsapp Gateway</NavItem>
                  <NavItem to="/admin/whatsapp-ai-settings" className="text-sm text-purple-400">🤖 AI Settings</NavItem>
                  <NavItem to="/admin/custom-sms-broadcast" className="text-sm text-blue-400">📢 SMS Broadcast</NavItem>
                  <NavItem to="/admin/users" className="text-sm">Admin Users</NavItem>
                </div>
              )}
            </div>

            <div className="pt-4 mt-4 border-t border-gray-800">
              <button
                onClick={toggleTheme}
                className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-gray-300 hover:bg-gray-800 rounded-md mb-2"
              >
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </button>
              <div className="px-3 py-2 text-sm text-gray-400 mb-2">
                {currentUser?.displayName || currentUser?.email}
              </div>
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-red-400 hover:bg-gray-800 rounded-md"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Page Content */}
      <div className="flex-1 overflow-x-hidden">
        <Outlet />
      </div>
    </div>
  );
}
